import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, MapPin, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/states`;

const EMPTY_FORM = { country_id: "", name: "", state_code: "", description: "" };

export default function StatesPage() {
  const { view, setView, list: states, inactiveCount, loading, error, reload } = useActiveInactive(BASE);
  const [countries, setCountries] = useState([]);

  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formErr, setFormErr]     = useState("");
  const [saving, setSaving]       = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/countries`, ax());
      setCountries(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", state: null }); };
  const openEdit = (s) => {
    setForm({ country_id: s.country_id, name: s.name, state_code: s.state_code, description: s.description || "" });
    setFormErr(""); setModal({ mode: "edit", state: s });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.country_id)        { setFormErr("Please select a country."); return; }
    if (!form.name.trim())       { setFormErr("Name is required."); return; }
    if (!form.state_code.trim()) { setFormErr("State code is required."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.state.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch (e) { alert(formatApiError(e, "Could not delete.")); setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">States</h1>
          <p className="page-sub">Manage states and provinces under each country.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} disabled={countries.length === 0} data-testid="add-state-btn">
            <Plus size={16} /> Add State
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="states-view" />

      {countries.length === 0 && !loading && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          No countries available. Please add a country first.
        </div>
      )}

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {states.length === 0 ? (
            <div className="empty-state">
              <MapPin size={32} color="#43E0FF" />
              {view === "active"
                ? <p>No states yet. Click <strong>Add State</strong> to create one.</p>
                : <p>No inactive states.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="states-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Country</th>
                  <th>State Name</th>
                  <th style={{ width: 130 }}>State Code</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {states.map((s, i) => (
                  <tr key={s.id} data-testid={`state-row-${s.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-desc">
                      <span className="subject-pill">
                        {s.country_code && <span className="subject-pill-code">{s.country_code}</span>}
                        {s.country_name}
                      </span>
                    </td>
                    <td className="td-name">{s.name}</td>
                    <td><span className="subject-pill-code">{s.state_code}</span></td>
                    <td className="td-desc">{s.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(s)}        data-testid={`edit-state-${s.id}`}   title="Edit"><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(s.id)} data-testid={`delete-state-${s.id}`} title="Delete"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(s.id)} disabled={restoringId === s.id} data-testid={`restore-state-${s.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="state-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add State" : "Edit State"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field">
                <label className="field-label">Country <span className="required">*</span></label>
                <select className="field-input" value={form.country_id}
                  onChange={onChange("country_id")} autoFocus data-testid="state-country-select">
                  <option value="">Select a country…</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.country_code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Name <span className="required">*</span></label>
                  <input className="field-input" placeholder="Karnataka" value={form.name}
                    onChange={onChange("name")} maxLength={120} data-testid="state-name-input" />
                </div>
                <div className="field">
                  <label className="field-label">State Code <span className="required">*</span></label>
                  <input className="field-input" placeholder="KA" value={form.state_code}
                    onChange={onChange("state_code")} maxLength={10}
                    style={{ textTransform: "uppercase" }} data-testid="state-code-input" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Optional notes"
                  value={form.description} onChange={onChange("description")} rows={3} maxLength={600}
                  data-testid="state-description-input" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-state-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add State" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete State</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this state to <strong>Inactive</strong>? You cannot deactivate a state that still has active cities.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
